import { DefaultLineItemGrouping, GeneralInvoicingSettings, InvoicingSettings, InvoicingSystemSettings } from "../../../domain/invoicing/settings/invoicingSettings"
import { QboAccount } from "../../../domain/quickbooks/quickbooksModel"
import { TaxRate } from "../../../domain/invoicing/taxRate"

export const getInvoicingSystemSettings = (allSettings: InvoicingSettings): InvoicingSystemSettings => {
  return {
    invoicingSystem: allSettings.invoicingSystem,
    defaultPaymentMethods: allSettings.defaultPaymentMethods,
    defaultAuthorizationForPaymentOnFile: allSettings.defaultAuthorizationForPaymentOnFile,
    qboDepositBankAccountId: allSettings.qboDepositBankAccountId,
    qboFeesExpenseAccountId: allSettings.qboFeesExpenseAccountId
  }
}

export const areSystemSettingsUpdated = (systemSettings: InvoicingSystemSettings, serverSettings: InvoicingSettings): boolean => {
  return systemSettings.invoicingSystem !== serverSettings.invoicingSystem
    || systemSettings.defaultPaymentMethods.ach !== serverSettings.defaultPaymentMethods.ach
    || systemSettings.defaultPaymentMethods.creditCard !== serverSettings.defaultPaymentMethods.creditCard
    || systemSettings.defaultAuthorizationForPaymentOnFile.showAuthorizePaymentOnFile !== serverSettings.defaultAuthorizationForPaymentOnFile.showAuthorizePaymentOnFile
    || systemSettings.defaultAuthorizationForPaymentOnFile.requireAuthorizePaymentOnFile !== serverSettings.defaultAuthorizationForPaymentOnFile.requireAuthorizePaymentOnFile
    || systemSettings.qboDepositBankAccountId !== serverSettings.qboDepositBankAccountId
    || systemSettings.qboFeesExpenseAccountId !== serverSettings.qboFeesExpenseAccountId
}

export const isSwitchingToQuickBooks = (systemSettings: InvoicingSystemSettings, serverSettings: InvoicingSettings): boolean => {
  return serverSettings.invoicingSystem === 'synkedUp' && systemSettings.invoicingSystem === 'quickBooks'
}

export const areQboAccountsRequired = (systemSettings: InvoicingSystemSettings, qboConnected: boolean): boolean => {
  return systemSettings.invoicingSystem === 'synkedUp' && qboConnected
}

export const areQboAccountsValid = (systemSettings: InvoicingSystemSettings, qboConnected: boolean): boolean => {
  if (!areQboAccountsRequired(systemSettings, qboConnected)) {
    return true
  }
  return !!systemSettings.qboDepositBankAccountId && !!systemSettings.qboFeesExpenseAccountId
}

const BANK_ASSET_TYPES = ['Bank', 'Other Current Asset']

export const isDepositBankAccount = (accountType: string): boolean => {
  return BANK_ASSET_TYPES.includes(accountType)
}

export const isFeesExpenseAccount = (accountType: string): boolean => {
  return accountType === 'Expense'
}

export type QboAccountOption = { value: string, label: string }

const toAccountOption = (account: QboAccount): QboAccountOption => ({ value: account.id, label: account.name })

export const getDepositAccountOptions = (accounts: QboAccount[]): QboAccountOption[] => {
  return accounts.filter(a => isDepositBankAccount(a.accountType)).map(toAccountOption)
}

export const getFeesExpenseAccountOptions = (accounts: QboAccount[]): QboAccountOption[] => {
  return accounts.filter(a => isFeesExpenseAccount(a.accountType)).map(toAccountOption)
}

export const areLineItemGroupingSettingsUpdated = (grouping: DefaultLineItemGrouping, serverSettings: DefaultLineItemGrouping): boolean => {
  return grouping.project !== serverSettings.project
    || grouping.maintenance !== serverSettings.maintenance
    || grouping.serviceTicket !== serverSettings.serviceTicket
}

export const getGeneralInvoicingSettings = (serverSettings: InvoicingSettings): GeneralInvoicingSettings => {
  return {
    invoicingAddress: {
      ...serverSettings.invoicingAddress,
      countryCode: serverSettings.invoicingAddress.countryCode || 'US'
    },
    supportEmail: serverSettings.supportEmail,
    defaultDueInDays: serverSettings.defaultDueInDays
  }
}


export const areGeneralInvoicingSettingsUpdated = (generalSettings: GeneralInvoicingSettings, serverSettings: InvoicingSettings): boolean => {
  return generalSettings.invoicingAddress.address1 !== serverSettings.invoicingAddress.address1
    || generalSettings.invoicingAddress.city !== serverSettings.invoicingAddress.city
    || generalSettings.invoicingAddress.state !== serverSettings.invoicingAddress.state
    || generalSettings.invoicingAddress.zip !== serverSettings.invoicingAddress.zip
    || generalSettings.invoicingAddress.countryCode !== serverSettings.invoicingAddress.countryCode
    || generalSettings.supportEmail !== serverSettings.supportEmail
    || generalSettings.defaultDueInDays !== serverSettings.defaultDueInDays
}

export type EnrichedTaxRate = TaxRate & {
  canMoveUp: boolean
  canMoveDown: boolean
}

export const enrichTaxRates = (taxRates: TaxRate[]): EnrichedTaxRate[] => {
  return taxRates
    .sort((a, b) => a.sortIndex - b.sortIndex)
    .map((x, index) => {
      return {
        ...x,
        canMoveUp: index > 0,
        canMoveDown: index < taxRates.length - 1
      }
    })
}

export const AUTOMATIC_TAX_ROW_ID = 'automatic-tax-row'

export type AutomaticTaxRateRow = {
  kind: 'automatic'
  id: string
  isDefault: boolean
  isActive: boolean
}

export type ManualTaxRateRow = EnrichedTaxRate & {
  kind: 'manual'
}

export type TaxRateTableRow = AutomaticTaxRateRow | ManualTaxRateRow

export const buildTaxRateTableRows = (taxRates: TaxRate[], showAutomaticRow: boolean, automaticIsDefault: boolean, automaticIsActive: boolean): TaxRateTableRow[] => {
  if (!showAutomaticRow) {
    return enrichTaxRates(taxRates).map((x): ManualTaxRateRow => ({ ...x, kind: 'manual' }))
  }
  const automaticHoldsDefault = automaticIsDefault && automaticIsActive
  const manualRows = enrichTaxRates(taxRates).map((x): ManualTaxRateRow => ({
    ...x,
    kind: 'manual',
    isDefault: !automaticHoldsDefault && x.isDefault
  }))
  return [{ kind: 'automatic', id: AUTOMATIC_TAX_ROW_ID, isDefault: automaticHoldsDefault, isActive: automaticIsActive }, ...manualRows]
}

export type TaxRateValidationFields = {
  name: string
  rate: number
}

export const getTaxRateErrors = (taxRate: TaxRateValidationFields): string[] => {
  const errors: string[] = []
  if (taxRate.name.trim() === '') {
    errors.push('Name is required')
  }
  if (taxRate.rate < 0) {
    errors.push('Rate must be greater than or equal to 0')
  }
  if (taxRate.rate > 1.0) {
    errors.push('Rate must be less than or equal to 100')
  }
  return errors
}